import { useRef, useEffect } from "react";
import Tooltip from "components/Tooltip";
import { useFundingRequestForm } from "./FundingRequestFormContext";

interface ControlledTooltipProps {
  id: string;
  message: string;
  position?: "left" | "center" | "right" | "down" | "up";
  children: React.ReactNode;
  wrapperClass?: string;
  disabled?: boolean;
  tooltipContainerClassName?: string;
}

/**
 * A tooltip that integrates with the FundingRequestForm context
 * to ensure only one tooltip is visible at a time.
 */
export default function ControlledTooltip({
  id,
  message,
  position = "down",
  children,
  wrapperClass,
  disabled = false,
  tooltipContainerClassName
}: Readonly<ControlledTooltipProps>) {
  const { activeTooltipId, setActiveTooltipId } = useFundingRequestForm();
  const isOpen = activeTooltipId === id;
  const containerRef = useRef<HTMLDivElement>(null);

  // Simulate mouse leave when another tooltip opens to force close
  useEffect(() => {
    if (!isOpen && containerRef.current) {
      // Trigger a synthetic mouse leave event to close the tooltip
      const mouseLeaveEvent = new MouseEvent("mouseleave", {
        bubbles: true,
        cancelable: true,
        view: window
      });
      containerRef.current.dispatchEvent(mouseLeaveEvent);
    }
  }, [isOpen]);

  const handleMouseEnter = (_e: React.MouseEvent) => {
    if (!disabled) {
      setActiveTooltipId(id);
    }
  };

  return (
    <div ref={containerRef} style={{ display: "contents" }}>
      <Tooltip
        message={message}
        position={position}
        wrapperClass={wrapperClass}
        disabled={disabled || !isOpen}
        tooltipContainerClassName={tooltipContainerClassName}
        handleMouseOver={handleMouseEnter}>
        {children}
      </Tooltip>
    </div>
  );
}
